import type { ClosingPackageJournalEntry } from '../types';

export type ClosingPackageJournalDetail = {
  label: string;
  value: string;
};

export type ClosingPackageJournalResultKind = 'success' | 'rejection' | 'problem' | 'neutral';

export type ClosingPackageJournalDisplayEntry = ClosingPackageJournalEntry & {
  actionLabel: string;
  actionColor: string;
  actorLabel: string;
  description: string;
  resultKind: ClosingPackageJournalResultKind;
  resultLabel: string;
  resultColor: string;
  details: ClosingPackageJournalDetail[];
};

const ACTION_META: Record<string, { label: string; color: string }> = {
  ADD: { label: 'Добавление документа', color: 'blue' },
  EXCLUDE: { label: 'Исключение документа', color: 'orange' },
  REPLACE: { label: 'Замена документа', color: 'cyan' },
  RESTORE: { label: 'Восстановление документа', color: 'green' },
  CONFIRM_FORMATION: { label: 'Формирование комплекта', color: 'blue' },
  ORIGINALS_REVISION_CONFIRMED: {
    label: 'Доработка оригиналов завершена',
    color: 'blue',
  },
  SCAN_DISPATCH_REGISTERED: { label: 'Отправка клиенту', color: 'processing' },
  CLIENT_APPROVED: { label: 'Комплект утверждён', color: 'success' },
  CLIENT_REJECTED: { label: 'Комплект отклонён', color: 'error' },
  ORIGINALS_PREPARED: { label: 'Оригиналы подготовлены', color: 'cyan' },
  ORIGINALS_DISPATCHED: { label: 'Оригиналы отправлены', color: 'processing' },
  CLIENT_RECEIVED: { label: 'Клиент получил оригиналы', color: 'success' },
  ORIGINALS_RECEIPT_PROBLEM: {
    label: 'Проблема с оригиналами',
    color: 'error',
  },
  EDO_SENT: { label: 'Отправка через ЭДО', color: 'processing' },
  EDO_RECEIPT_CONFIRMED: { label: 'Принятие по ЭДО', color: 'success' },
  EDO_CONTENT_REJECTED: { label: 'Доработка комплекта по ЭДО', color: 'error' },
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function displayJson(value: unknown): string {
  if (value == null) {
    return '—';
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function itemLabel(
  entry: ClosingPackageJournalEntry,
  itemLabels: Readonly<Record<string, string>>
): string {
  if (!entry.itemKey) {
    return 'комплект';
  }
  return itemLabels[entry.itemKey] ?? entry.itemKey;
}

function isInitialComposition(entry: ClosingPackageJournalEntry): boolean {
  return (
    entry.action === 'ADD' &&
    (entry.comment === 'INITIAL_COMPOSITION_CREATED' ||
      asRecord(entry.newValue)?.kind === 'INITIAL_COMPOSITION_CREATED')
  );
}

function describeEntry(
  entry: ClosingPackageJournalEntry,
  itemLabels: Readonly<Record<string, string>>
): string {
  const label = itemLabel(entry, itemLabels);
  const newValue = asRecord(entry.newValue);

  if (isInitialComposition(entry)) {
    const count = Number(newValue?.itemCount ?? 0);
    return `Создан начальный состав: ${count} поз.`;
  }

  switch (entry.action) {
    case 'ADD':
      return `Документ${entry.documentId ? ` #${entry.documentId}` : ''} добавлен в «${label}»`;
    case 'EXCLUDE':
      return `Позиция «${label}» исключена из комплекта`;
    case 'REPLACE':
      return `Документ в позиции «${label}» заменён`;
    case 'RESTORE':
      return `Позиция «${label}» восстановлена в комплекте`;
    case 'CONFIRM_FORMATION':
      return `Состав комплекта подтверждён (${Number(newValue?.itemCount ?? 0)} поз.)`;
    case 'ORIGINALS_REVISION_CONFIRMED':
      return 'Доработанный состав оригиналов подтверждён';
    case 'SCAN_DISPATCH_REGISTERED':
      return 'Электронный комплект отправлен клиенту на согласование';
    case 'CLIENT_APPROVED':
      return 'Получен положительный ответ клиента';
    case 'CLIENT_REJECTED':
      return 'Получены замечания клиента, создан цикл доработки';
    case 'ORIGINALS_PREPARED':
      return 'Бумажные оригиналы подготовлены к отправке';
    case 'ORIGINALS_DISPATCHED':
      return 'Бумажные оригиналы отправлены клиенту';
    case 'CLIENT_RECEIVED':
      return 'Клиент получил оригиналы';
    case 'EDO_SENT':
      return 'Документы отправлены клиенту через ЭДО';
    case 'EDO_RECEIPT_CONFIRMED':
      return 'Клиент принял документы по ЭДО';
    case 'EDO_CONTENT_REJECTED':
      return entry.comment?.trim()
        ? `Требуется исправление комплекта: ${entry.comment.trim()}`
        : 'Требуется исправление комплекта по ЭДО';
    case 'ORIGINALS_RECEIPT_PROBLEM':
      return 'Зафиксирована проблема получения, создан новый цикл оригиналов';
    default:
      return entry.comment || `Операция ${entry.action}`;
  }
}

function buildDetails(entry: ClosingPackageJournalEntry): ClosingPackageJournalDetail[] {
  const details: ClosingPackageJournalDetail[] = [];

  if (entry.itemKey) {
    details.push({ label: 'Ключ позиции', value: entry.itemKey });
  }
  if (entry.routeId != null) {
    details.push({ label: 'Маршрут', value: `#${entry.routeId}` });
  }
  if (entry.previousDocumentId != null) {
    details.push({ label: 'Предыдущий документ', value: `#${entry.previousDocumentId}` });
  }
  if (entry.documentId != null) {
    details.push({ label: 'Документ', value: `#${entry.documentId}` });
  }
  if (entry.comment && entry.comment !== 'INITIAL_COMPOSITION_CREATED') {
    details.push({ label: 'Комментарий', value: entry.comment });
  }
  if (entry.oldValue != null) {
    details.push({ label: 'До изменения', value: displayJson(entry.oldValue) });
  }
  if (entry.newValue != null) {
    details.push({ label: 'После изменения', value: displayJson(entry.newValue) });
  }

  return details;
}

const RESULT_META: Record<
  ClosingPackageJournalResultKind,
  { label: string; color: string }
> = {
  success: { label: 'Успешно', color: 'success' },
  rejection: { label: 'Доработка', color: 'error' },
  problem: { label: 'Проблема', color: 'error' },
  neutral: { label: 'Зафиксировано', color: 'default' },
};

function resolveJournalResultKind(
  entry: ClosingPackageJournalEntry
): ClosingPackageJournalResultKind {
  switch (entry.action) {
    case 'CLIENT_REJECTED':
    case 'EDO_CONTENT_REJECTED':
      return 'rejection';
    case 'ORIGINALS_RECEIPT_PROBLEM':
      return 'problem';
    case 'CLIENT_APPROVED':
    case 'CLIENT_RECEIVED':
    case 'EDO_SENT':
    case 'EDO_RECEIPT_CONFIRMED':
    case 'ORIGINALS_DISPATCHED':
    case 'ORIGINALS_PREPARED':
    case 'CONFIRM_FORMATION':
    case 'SCAN_DISPATCH_REGISTERED':
    case 'ORIGINALS_REVISION_CONFIRMED':
    case 'ADD':
    case 'EXCLUDE':
    case 'REPLACE':
    case 'RESTORE':
      return 'success';
    default:
      return ACTION_META[entry.action] ? 'success' : 'neutral';
  }
}

export function buildClosingPackageJournalDisplayEntries(
  entries: ClosingPackageJournalEntry[],
  itemLabels: Readonly<Record<string, string>>
): ClosingPackageJournalDisplayEntry[] {
  return entries.map((entry) => {
    const meta = ACTION_META[entry.action] ?? {
      label: entry.action,
      color: 'default',
    };
    const resultKind = resolveJournalResultKind(entry);
    const result = RESULT_META[resultKind];

    return {
      ...entry,
      actionLabel: isInitialComposition(entry) ? 'Создание комплекта' : meta.label,
      actionColor: isInitialComposition(entry) ? 'blue' : meta.color,
      actorLabel: entry.userName || (entry.userId ? `Пользователь #${entry.userId}` : 'Система'),
      description: describeEntry(entry, itemLabels),
      resultKind,
      resultLabel: result.label,
      resultColor: result.color,
      details: buildDetails(entry),
    };
  });
}

